import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Save, Clock, ListChecks, Pencil, RefreshCcw, Upload, Image as ImageIcon, Loader2, Database, Users as UsersIcon, Download, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Time24Input } from "@/components/Time24Input";

const CATEGORIES = ["Preventive", "Restorative", "Endodontic", "Prosthetic", "Surgical", "Cosmetic", "Other"];
const CAT_COLORS = {
  Preventive:  "bg-[#E8F0EB] text-[#2D6A4F]",
  Restorative: "bg-[#FDF3ED] text-[#B0521F]",
  Endodontic:  "bg-[#F0EBE1] text-[#7A5C1C]",
  Prosthetic:  "bg-[#E5E2F0] text-[#4A3D8A]",
  Surgical:    "bg-[#F0E5E5] text-[#8A2D2D]",
  Cosmetic:    "bg-[#E5F0EE] text-[#1F6B69]",
  Other:       "bg-linen text-ink",
};

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];
const DEFAULT_HOURS = {
  monday: { open: "09:00", close: "18:00", closed: false },
  tuesday: { open: "09:00", close: "18:00", closed: false },
  wednesday: { open: "09:00", close: "18:00", closed: false },
  thursday: { open: "09:00", close: "18:00", closed: false },
  friday: { open: "09:00", close: "18:00", closed: false },
  saturday: { open: "10:00", close: "16:00", closed: false },
  sunday: { open: "10:00", close: "14:00", closed: true },
};

export default function Settings() {
  const { user, refresh: refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [invites, setInvites] = useState([]);
  const [team, setTeam] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "receptionist" });
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [savingHours, setSavingHours] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", variant: "", category: "Other", cost: 0, duration_min: 30, notes: "", show_pricing_online: null });

  const load = useCallback(async () => {
    const [t, me, c, cat] = await Promise.all([
      api.get("/team"),
      api.get("/auth/me"),
      api.get("/clinics/me"),
      api.get("/procedure-catalog"),
    ]);
    setTeam(t.data);
    setClinic(c.data);
    setProfile({});
    setHours({ ...DEFAULT_HOURS, ...(c.data?.working_hours || {}) });
    setCatalog(cat.data);
    if (isAdmin) {
      const inv = await api.get("/invites");
      setInvites(inv.data);
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invites", form);
      toast.success("Invite created — copy and share the link");
      setOpen(false); setForm({ email: "", role: "receptionist" });
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/signup?invite=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  const revoke = async (id) => {
    await api.delete(`/invites/${id}`);
    toast.success("Invite revoked"); load();
  };

  const toggleDoctorPricing = async (memberId, value) => {
    try {
      await api.patch(`/team/${memberId}`, { show_pricing_online: value });
      toast.success("Doctor visibility updated");
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const clean = Object.fromEntries(Object.entries(profile).filter(([, v]) => v !== "" && v !== undefined));
      await api.patch("/clinics/me", clean);
      toast.success("Clinic profile saved");
      await Promise.all([load(), refreshUser?.()]);
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setSavingProfile(false); }
  };

  const updateDay = (day, field, value) => {
    setHours((h) => ({ ...h, [day]: { ...h[day], [field]: value } }));
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      await api.patch("/clinics/me", { working_hours: hours });
      toast.success("Working hours saved");
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setSavingHours(false); }
  };

  // ---- Procedure catalog handlers ----
  const openCatalogNew = () => {
    setEditingItem(null);
    setCatForm({ name: "", variant: "", category: "Other", cost: 0, duration_min: 30, notes: "", show_pricing_online: null });
    setCatalogOpen(true);
  };
  const openCatalogEdit = (item) => {
    setEditingItem(item);
    setCatForm({
      name: item.name || "", variant: item.variant || "",
      category: item.category || "Other", cost: item.cost || 0,
      duration_min: item.duration_min || 30, notes: item.notes || "",
      show_pricing_online: item.show_pricing_online ?? null,
    });
    setCatalogOpen(true);
  };
  const saveCatalog = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: catForm.name.trim(),
        variant: catForm.variant.trim() || null,
        category: catForm.category,
        cost: Number(catForm.cost),
        duration_min: Number(catForm.duration_min) || null,
        notes: catForm.notes || null,
        active: true,
        // null ⇒ inherit clinic-wide "show pricing" setting.
        show_pricing_online: catForm.show_pricing_online,
      };
      if (editingItem) {
        await api.patch(`/procedure-catalog/${editingItem.id}`, payload);
        toast.success("Procedure updated");
      } else {
        await api.post("/procedure-catalog", payload);
        toast.success("Procedure added");
      }
      setCatalogOpen(false);
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };
  const deleteCatalog = async (id) => {
    if (!confirm("Remove this procedure from the price list?")) return;
    try {
      await api.delete(`/procedure-catalog/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };
  const resetCatalog = async () => {
    if (!confirm("This will replace ALL procedures with the default price list. Continue?")) return;
    try {
      const { data } = await api.post("/procedure-catalog/reset-defaults", {});
      toast.success(`Reset · ${data.seeded} default procedures loaded`);
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const catalogByCategory = useMemo(() => {
    const g = {};
    for (const c of CATEGORIES) g[c] = [];
    for (const item of catalog) {
      const cat = g[item.category] ? item.category : "Other";
      g[cat].push(item);
    }
    return g;
  }, [catalog]);

  const bind = (k) => ({
    value: profile[k] ?? clinic?.[k] ?? "",
    onChange: (e) => setProfile((p) => ({ ...p, [k]: e.target.value })),
  });

  return (
    <div>
      <span className="overline text-ink-soft">Settings</span>
      <h1 className="mt-2 font-display text-4xl tracking-tight">Clinic settings.</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appearance & Customization entry */}
        <Link
          to="/app/settings/appearance"
          data-testid="link-appearance"
          className="card-widget lg:col-span-2 bg-gradient-to-br from-white via-white to-linen/40 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)] hover:shadow-[0_14px_36px_-18px_rgba(26,54,43,0.22)] hover:border-pine/25 transition-all duration-300 group cursor-pointer flex items-center gap-4"
        >
          <div className="w-11 h-11 rounded-2xl bg-linen text-ink grid place-items-center ring-1 ring-inset ring-pine/10 shadow-inner shrink-0 group-hover:scale-105 transition-transform">
            <ImageIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink">Appearance &amp; customization</div>
            <div className="text-xs text-ink-mid mt-0.5">
              Theme mode, primary color, preset themes, corner radius, density, font, animations, accessibility.
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-ink-soft group-hover:text-ink-mid group-hover:translate-x-1 transition-all" />
        </Link>

        {/* Google Calendar integration entry */}
        <Link
          to="/app/settings/calendar"
          data-testid="link-calendar-integration"
          className="card-widget lg:col-span-2 bg-gradient-to-br from-white via-white to-linen/40 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)] hover:shadow-[0_14px_36px_-18px_rgba(26,54,43,0.22)] hover:border-pine/25 transition-all duration-300 group cursor-pointer flex items-center gap-4"
        >
          <div className="w-11 h-11 rounded-2xl bg-linen text-ink grid place-items-center ring-1 ring-inset ring-pine/10 shadow-inner shrink-0 group-hover:scale-105 transition-transform">
            <CalendarIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink">Calendar sync (Google Calendar)</div>
            <div className="text-xs text-ink-mid mt-0.5">
              Connect your Google Calendar for two-way sync. External busy time (vacations, conferences) auto-blocks online bookings.
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-ink-soft group-hover:text-ink-mid group-hover:translate-x-1 transition-all" />
        </Link>

        {/* Clinic logo */}
        <div className="card-widget lg:col-span-2" id="section-branding">
          <SettingsLogo clinic={clinic} isAdmin={isAdmin} onChange={load} />
        </div>

        {/* Clinic profile / GST */}
        <div className="card-widget lg:col-span-2" id="section-clinic">
          <div className="overline text-ink-soft mb-3">Clinic & GST profile (for invoices)</div>
          <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="clinic-profile-form">
            <input placeholder="Clinic name" data-testid="cp-name" disabled={!isAdmin} {...bind("name")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            <input placeholder="State (e.g. Maharashtra)" data-testid="cp-state" disabled={!isAdmin} {...bind("state")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            <input placeholder="Registered address" data-testid="cp-address" disabled={!isAdmin} {...bind("address")}
              className="md:col-span-2 rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            <input placeholder="GSTIN (15 chars, e.g. 27AABCU9603R1ZM)" data-testid="cp-gstin" disabled={!isAdmin} {...bind("gstin")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 font-mono disabled:bg-linen" />
            <input placeholder="PAN" data-testid="cp-pan" disabled={!isAdmin} {...bind("pan")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 font-mono disabled:bg-linen" />
            <input placeholder="Phone" data-testid="cp-phone" disabled={!isAdmin} {...bind("phone")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            <input placeholder="Public email" data-testid="cp-email" disabled={!isAdmin} {...bind("email")}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            <input placeholder="Invoice prefix (e.g. DAI → DAI-2026-0001)" data-testid="cp-prefix" disabled={!isAdmin} {...bind("invoice_prefix")}
              className="md:col-span-2 rounded-xl border border-[#E8EAE9] px-4 py-2.5 disabled:bg-linen" />
            {isAdmin && (
              <button data-testid="cp-save" disabled={savingProfile}
                className="md:col-span-2 btn-primary inline-flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> {savingProfile ? "Saving…" : "Save profile"}
              </button>
            )}
          </form>
        </div>

        {/* Invoice preferences — GST vs Non-GST */}
        <div className="card-widget lg:col-span-2" id="section-invoice-gst" data-testid="invoice-preferences-card">
          <div className="overline text-ink-soft mb-1">Billing → Invoice Preferences</div>
          <p className="text-xs text-ink-mid mb-4">
            Switch between GST-compliant tax invoices and clean non-GST invoices. Changes apply to
            <b> new invoices only</b> — previously generated invoices are never modified.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { id: "gst", title: "GST Invoice", desc: "Tax invoice with GSTIN, HSN, CGST/SGST breakdown. Ideal for GST-registered clinics." },
              { id: "non_gst", title: "Non-GST Invoice", desc: "Clean invoice without tax lines or GSTIN. Ideal for clinics under the GST threshold." },
            ].map((opt) => {
              const active = (clinic?.invoice_type || (clinic?.gstin ? "gst" : "non_gst")) === opt.id;
              const disabled = !isAdmin || (opt.id === "gst" && !clinic?.gstin);
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`invoice-type-${opt.id}`}
                  disabled={disabled}
                  onClick={async () => {
                    if (opt.id === "gst" && !clinic?.gstin) {
                      return toast.error("Please add a valid GST Number before enabling GST invoices.");
                    }
                    try {
                      await api.patch("/clinics/me", { invoice_type: opt.id });
                      toast.success(`Switched to ${opt.title}`);
                      load();
                    } catch (err) {
                      toast.error(apiError(err, "Could not update invoice type"));
                    }
                  }}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    active ? "border-pine bg-linen" : "border-[#E8EAE9] hover:border-pine/40"
                  } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display text-base">{opt.title}</div>
                    {active && <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest bg-pine text-linen">Active</span>}
                  </div>
                  <div className="text-xs text-ink-mid mt-1">{opt.desc}</div>
                  {opt.id === "gst" && !clinic?.gstin && (
                    <div className="text-[11px] text-[#B0521F] mt-2">Add a GSTIN above to unlock GST invoices.</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Invoice template — Patient Account Summary toggles */}
        <div className="card-widget lg:col-span-2" id="section-invoice-template" data-testid="invoice-template-card">
          <div className="overline text-ink-soft mb-1">Billing → Invoice Template</div>
          <p className="text-xs text-ink-mid mb-4">
            Choose what appears on every Print / PDF / Email / WhatsApp invoice. The
            <b> Patient Account Summary</b> nudges patients about outstanding dues on every bill.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { key: "show_account_summary",       label: "Show Patient Account Summary",
                hint: "Master switch. When off, none of the account summary block is rendered." },
              { key: "show_previous_outstanding",  label: "Show Previous Outstanding",
                hint: "The amount the patient owed before this invoice." },
              { key: "show_current_outstanding",   label: "Show Current Outstanding",
                hint: "Live balance across all invoices — highlighted in an amber accent box." },
              { key: "show_wallet_usage",          label: "Show Wallet Usage",
                hint: "Row appears only when wallet credit was applied to this invoice." },
              { key: "show_payment_status",        label: "Show Payment Status",
                hint: "Green ✓ / amber badge showing fully-paid, partially-paid, or unpaid." },
            ].map((opt) => {
              const tpl = clinic?.invoice_template || {};
              const checked = tpl[opt.key] !== false;   // default ON
              return (
                <label key={opt.key}
                       data-testid={`tpl-${opt.key.replace(/_/g,'-')}`}
                       className={`flex items-start gap-3 p-3 rounded-xl border ${
                         checked ? "border-pine/40 bg-linen" : "border-[#E8EAE9] hover:border-pine/30"
                       } cursor-pointer`}>
                  <input type="checkbox" className="mt-1 accent-pine"
                         disabled={!isAdmin}
                         checked={checked}
                         onChange={async (e) => {
                           try {
                             await api.patch("/clinics/me",
                               { invoice_template: { [opt.key]: e.target.checked } });
                             toast.success("Saved");
                             load();
                           } catch (err) {
                             toast.error(apiError(err, "Save failed"));
                           }
                         }} />
                  <div>
                    <div className="text-sm font-medium text-ink">{opt.label}</div>
                    <div className="text-xs text-ink-mid mt-0.5">{opt.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Working hours */}
        <div className="card-widget lg:col-span-2" id="section-hours" data-testid="working-hours-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 overline text-ink-soft">
                <Clock className="w-3.5 h-3.5" /> Working hours
              </div>
              <p className="mt-2 text-xs text-ink-mid max-w-lg">Set your clinic&apos;s weekly schedule. These hours drive the Calendar grid and constrain the AI when it suggests appointment slots.</p>
            </div>
            {isAdmin && (
              <button data-testid="wh-save" onClick={saveHours} disabled={savingHours}
                className="btn-primary text-xs inline-flex items-center gap-2 self-start">
                <Save className="w-3.5 h-3.5" /> {savingHours ? "Saving…" : "Save hours"}
              </button>
            )}
          </div>
          <div className="divide-y divide-[#E8EAE9]">
            {DAYS.map(({ key, label }) => {
              const row = hours[key] || DEFAULT_HOURS[key];
              return (
                <div key={key} data-testid={`wh-row-${key}`} className="py-3 flex items-center gap-4 flex-wrap">
                  <div className="w-28 text-sm font-medium text-ink">{label}</div>
                  <label className="inline-flex items-center gap-2 text-xs text-ink-mid cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`wh-${key}-closed`}
                      disabled={!isAdmin}
                      checked={!row.closed}
                      onChange={(e) => updateDay(key, "closed", !e.target.checked)}
                      className="w-4 h-4 accent-pine"
                    />
                    <span>{row.closed ? "Closed" : "Open"}</span>
                  </label>
                  <div className={`flex items-center gap-2 text-sm transition-opacity ${row.closed ? "opacity-40 pointer-events-none" : ""}`}>
                    <Time24Input
                      data-testid={`wh-${key}-open`}
                      disabled={!isAdmin || row.closed}
                      value={row.open}
                      onChange={(v) => updateDay(key, "open", v)}
                      className="rounded-lg border border-[#E8EAE9] px-3 py-1.5 font-mono text-xs disabled:bg-linen w-[72px] text-center"
                    />
                    <span className="text-ink-soft">to</span>
                    <Time24Input
                      data-testid={`wh-${key}-close`}
                      disabled={!isAdmin || row.closed}
                      value={row.close}
                      onChange={(v) => updateDay(key, "close", v)}
                      className="rounded-lg border border-[#E8EAE9] px-3 py-1.5 font-mono text-xs disabled:bg-linen w-[72px] text-center"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Procedure Catalog / Price list */}
        <div className="card-widget lg:col-span-2" id="section-procedures" data-testid="procedure-catalog-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 overline text-ink-soft">
                <ListChecks className="w-3.5 h-3.5" /> Price list · Procedure catalog
              </div>
              <p className="mt-2 text-xs text-ink-mid max-w-lg">Your clinic&apos;s custom price list. Crown variants, restorative options, procedure durations — set once, applied everywhere (Treatments, Invoices, AI slot suggestions).</p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={resetCatalog} data-testid="btn-reset-catalog"
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-pine/20 text-ink-mid hover:bg-linen">
                  <RefreshCcw className="w-3.5 h-3.5" /> Reset to defaults
                </button>
                <button onClick={openCatalogNew} data-testid="btn-add-procedure"
                  className="btn-primary text-xs inline-flex items-center gap-2">
                  <Plus className="w-3.5 h-3.5" /> Add procedure
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {CATEGORIES.map((cat) => {
              const items = catalogByCategory[cat] || [];
              if (items.length === 0) return null;
              return (
                <div key={cat} data-testid={`catalog-cat-${cat.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${CAT_COLORS[cat]}`}>{cat}</span>
                    <span className="text-[10px] text-ink-soft">{items.length} procedure{items.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="rounded-2xl border border-[#E8EAE9] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-linen/50 text-left text-ink-soft overline text-[10px]">
                          <th className="px-4 py-2">Procedure</th>
                          <th className="px-4 py-2">Variant</th>
                          <th className="px-4 py-2 text-right">Cost</th>
                          <th className="px-4 py-2 text-right">Duration</th>
                          {isAdmin && <th className="px-4 py-2 w-24"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it.id} data-testid={`catalog-row-${it.id}`} className="border-t border-[#E8EAE9]">
                            <td className="px-4 py-2.5 font-medium text-ink">{it.name}</td>
                            <td className="px-4 py-2.5 text-ink-mid">{it.variant || "—"}</td>
                            <td className="px-4 py-2.5 text-right font-mono">
                              ₹{Number(it.cost).toLocaleString("en-IN")}
                              {it.show_pricing_online === false && (
                                <span className="ml-2 inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5E0D2] text-[#B0521F]" title="This fee is hidden on the public booking page">
                                  hidden
                                </span>
                              )}
                              {it.show_pricing_online === true && (
                                <span className="ml-2 inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#E8F0EB] text-[#2D6A4F]" title="This fee is always shown on the public booking page">
                                  shown
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right text-ink-mid text-xs">{it.duration_min ? `${it.duration_min} min` : "—"}</td>
                            {isAdmin && (
                              <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                <button data-testid={`edit-catalog-${it.id}`} onClick={() => openCatalogEdit(it)} className="text-ink-mid hover:text-ink p-1">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button data-testid={`del-catalog-${it.id}`} onClick={() => deleteCatalog(it.id)} className="text-[#B0521F]/60 hover:text-[#B0521F] p-1 ml-1">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            {catalog.length === 0 && (
              <div className="text-center py-8 text-sm text-ink-soft">No procedures yet. Click &ldquo;Reset to defaults&rdquo; for a starter list.</div>
            )}
          </div>

          {isAdmin && (
            <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editingItem ? "Edit procedure" : "New procedure"}</DialogTitle></DialogHeader>
                <form onSubmit={saveCatalog} className="space-y-3" data-testid="catalog-form">
                  <div className="grid grid-cols-2 gap-3">
                    <input required data-testid="cat-name" placeholder="Name (e.g., Crown)" value={catForm.name}
                      onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                      className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                    <input data-testid="cat-variant" placeholder="Variant (e.g., Zirconia, optional)" value={catForm.variant}
                      onChange={(e) => setCatForm({ ...catForm, variant: e.target.value })}
                      className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                  </div>
                  <select data-testid="cat-category" value={catForm.category}
                    onChange={(e) => setCatForm({ ...catForm, category: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft text-sm">₹</span>
                      <input required data-testid="cat-cost" type="number" min={0} placeholder="Cost" value={catForm.cost}
                        onChange={(e) => setCatForm({ ...catForm, cost: e.target.value })}
                        className="w-full rounded-xl border border-[#E8EAE9] pl-7 pr-4 py-2.5 text-sm" />
                    </div>
                    <input data-testid="cat-duration" type="number" min={0} placeholder="Duration (min)" value={catForm.duration_min}
                      onChange={(e) => setCatForm({ ...catForm, duration_min: e.target.value })}
                      className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                  </div>
                  <textarea data-testid="cat-notes" placeholder="Notes (optional)" value={catForm.notes}
                    onChange={(e) => setCatForm({ ...catForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm resize-none" />
                  <div className="rounded-xl border border-[#EFEADC] bg-[#FBFAF6] p-3 text-xs">
                    <div className="font-medium text-ink mb-1">Pricing on public booking page</div>
                    <div className="text-ink-mid mb-2">Overrides the clinic-wide setting for this one procedure.</div>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { v: null, label: "Follow clinic default" },
                        { v: true, label: "Always show fee" },
                        { v: false, label: "Always hide fee" },
                      ].map((opt) => (
                        <button
                          key={String(opt.v)}
                          type="button"
                          data-testid={`cat-vis-${opt.v === null ? "inherit" : opt.v ? "show" : "hide"}`}
                          onClick={() => setCatForm({ ...catForm, show_pricing_online: opt.v })}
                          className={`px-3 py-1.5 rounded-full border text-xs ${
                            catForm.show_pricing_online === opt.v
                              ? "bg-pine text-linen border-pine"
                              : "bg-white text-ink border-[#E8EAE9] hover:border-pine"
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button data-testid="cat-submit" className="w-full btn-primary text-sm">Save</button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Team */}
        <div className="card-widget lg:col-span-2" id="section-team">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-ink-soft">Team</div>
            {isAdmin && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="btn-invite-teammate" className="btn-primary text-xs inline-flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Invite teammate</button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader><DialogTitle className="font-display text-2xl">Invite teammate</DialogTitle></DialogHeader>
                  <form onSubmit={create} className="space-y-3" data-testid="invite-form">
                    <input required data-testid="invite-email" type="email" placeholder="Email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                    <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                      <option value="admin">Admin</option>
                      <option value="doctor">Doctor</option>
                      <option value="receptionist">Receptionist</option>
                    </select>
                    <button data-testid="invite-submit" className="w-full btn-primary text-sm">Create invite</button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft overline"><th className="py-2">Name</th><th>Email</th><th>Role</th><th>Joined</th><th>Booking pricing</th></tr>
            </thead>
            <tbody>
              {team.map((m) => (
                <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-t border-[#E8EAE9]">
                  <td className="py-3 font-medium">{m.name}</td>
                  <td className="text-ink-mid">{m.email}</td>
                  <td><span className="text-xs px-3 py-1 rounded-full bg-linen">{m.role}</span></td>
                  <td className="text-ink-mid">{m.created_at?.slice(0,10)}</td>
                  <td>
                    {m.role === "doctor" ? (
                      isAdmin ? (
                        <label className="inline-flex items-center gap-2 text-xs text-ink-mid cursor-pointer"
                               data-testid={`doctor-pricing-toggle-${m.id}`}>
                          <input type="checkbox" className="accent-pine"
                                 checked={m.show_pricing_online !== false}
                                 onChange={(e) => toggleDoctorPricing(m.id, e.target.checked)} />
                          {m.show_pricing_online !== false ? "Visible" : "Hidden"}
                        </label>
                      ) : (
                        <span className="text-xs text-ink-soft">{m.show_pricing_online !== false ? "Visible" : "Hidden"}</span>
                      )
                    ) : (
                      <span className="text-xs text-ink-disabled">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isAdmin && (
          <div className="card-widget lg:col-span-2">
            <div className="overline text-ink-soft mb-3">Pending invites</div>
            <div className="divide-y divide-[#E8EAE9]">
              {invites.length === 0 && <div className="py-6 text-sm text-ink-soft text-center">No pending invites.</div>}
              {invites.map((i) => (
                <div key={i.id} data-testid={`invite-row-${i.id}`} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.email}</div>
                    <div className="text-xs text-ink-mid">{i.role} · {i.used ? "used" : "pending"}</div>
                  </div>
                  <code className="text-xs bg-linen px-2 py-1 rounded max-w-[220px] truncate">{i.token}</code>
                  <button data-testid={`copy-invite-${i.id}`} onClick={() => copyLink(i.token)} className="text-ink hover:text-ink-mid" title="Copy invite link">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button data-testid={`revoke-invite-${i.id}`} onClick={() => revoke(i.id)} className="text-[#B94A48] hover:text-[#8A2E2C]" title="Revoke">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div id="data-management" className="card-widget lg:col-span-2 scroll-mt-24" data-testid="settings-data-management-card">
          <div className="flex items-center gap-2 overline text-ink-soft mb-1">
            <Database className="w-3.5 h-3.5" /> Data Management
          </div>
          <p className="text-xs text-ink-mid mb-4 max-w-2xl">
            Administrative and maintenance utilities for your clinic&rsquo;s data. Keep your patient database
            clean and export a full archive whenever you need one.
          </p>

          <div className="divide-y divide-[#EFEADC] rounded-2xl border border-[#E8EAE9] overflow-hidden bg-white">
            {/* Duplicate Patients */}
            {isAdmin && (
              <Link
                to="/app/settings/data-management/duplicates"
                data-testid="link-duplicate-patients"
                className="flex items-center gap-4 px-4 py-3.5 hover:bg-linen/60 transition-colors group"
              >
                <div className="w-9 h-9 rounded-full bg-[#FDF3ED] text-[#8A3F1E] grid place-items-center shrink-0">
                  <UsersIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink">Duplicate Patients</div>
                  <div className="text-xs text-ink-mid">
                    Scan and merge patients sharing a phone, email, or name + DOB. Nothing is lost.
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-ink-soft group-hover:text-ink transition-colors" />
              </Link>
            )}

            {/* Export Data / Backup */}
            <div className="flex items-center gap-4 px-4 py-3.5">
              <div className="w-9 h-9 rounded-full bg-[#EEF1F5] text-[#28527A] grid place-items-center shrink-0">
                <Download className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink">Export Data</div>
                <div className="text-xs text-ink-mid">
                  Download a complete JSON archive of your clinic — patients, appointments, invoices,
                  consents, warranties, medical history. Complies with your DPDP right to data portability.
                </div>
              </div>
              <button
                data-testid="btn-export-backup"
                onClick={async () => {
                  try {
                    const { data } = await api.get("/backup/export");
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `viyora-backup-${new Date().toISOString().slice(0, 10)}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Backup downloaded");
                  } catch (err) {
                    toast.error(apiError(err, "Backup failed"));
                  }
                }}
                className="btn-primary text-xs whitespace-nowrap shrink-0"
              >
                Download
              </button>
            </div>
          </div>
        </div>

        <div className="card-widget lg:col-span-2">
          <div className="overline text-ink-soft mb-3">Integrations</div>
          <div className="text-xs text-ink-mid">
            SMS (Twilio) and Email (Resend) delivery is currently <strong>MOCKED</strong>. Add API keys to <code className="bg-linen px-1.5 py-0.5 rounded">/app/backend/.env</code> and wire in the <code>/api/reminders/send</code> handler to enable live sending.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SettingsLogo — upload / replace / remove the clinic logo. Admins only.
 * Uses the same POST /clinic/logo endpoint as onboarding, then triggers a
 * parent reload so the sidebar picks up the change on the next auth refresh.
 */
function SettingsLogo({ clinic, isAdmin, onChange }) {
  const { refresh: refreshUser } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const raw = clinic?.logo_url;
  const src = raw
    ? (raw.startsWith("http") ? raw : `${backend}${raw}?t=${clinic?.logo_updated_at || ""}`)
    : "";

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Please upload a PNG, JPG or WEBP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/clinic/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      await refreshUser?.();
      await onChange?.();
    } catch (err) {
      toast.error(apiError(err, "Could not upload logo"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your clinic logo? Your dashboard will show the Viyora mark again.")) return;
    try {
      await api.delete("/clinic/logo");
      toast.success("Logo removed");
      await refreshUser?.();
      await onChange?.();
    } catch (err) {
      toast.error(apiError(err, "Could not remove logo"));
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="settings-logo-block">
      <div className="w-24 h-24 rounded-2xl bg-linen border border-[#E8EAE9] grid place-items-center overflow-hidden shrink-0">
        {src ? (
          <img src={src} alt="Clinic logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" />
        ) : (
          <ImageIcon className="w-8 h-8 text-ink-disabled" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="overline text-ink-soft">Clinic logo</div>
        <div className="mt-1 text-sm font-medium text-ink">
          {raw ? "Your logo is live" : "No logo uploaded yet"}
        </div>
        <div className="mt-0.5 text-xs text-ink-mid max-w-lg">
          Shown on your patient booking page, the dashboard sidebar and future invoices. PNG or JPG, up to 2 MB. Square works best.
        </div>
        {isAdmin && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="settings-logo-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : raw ? "Replace logo" : "Upload logo"}
            </button>
            {raw && (
              <button
                type="button"
                onClick={remove}
                data-testid="settings-logo-remove-btn"
                className="text-xs text-ink-mid hover:text-[#B94A48]"
              >
                Remove
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="settings-logo-input"
            />
          </div>
        )}
      </div>
    </div>
  );
}

